import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

type Symptom = {
  _id?: string;
  date: string; // ISO
  category: string;
  severity: number; // 0-10
  note?: string;
};

const CATEGORIES: { value: string; label: string; help?: string }[] = [
  { value: 'nausea', label: 'Nausea' },
  { value: 'vomiting', label: 'Vomiting' },
  { value: 'diarrhea', label: 'Diarrhea' },
  { value: 'constipation', label: 'Constipation' },
  { value: 'headache', label: 'Headache' },
  { value: 'fatigue', label: 'Fatigue' },
  { value: 'acid_reflux', label: 'Acid Reflux' },
  { value: 'appetite_suppression', label: 'Reduced Appetite' },
  { value: 'injection_site_pain', label: 'Injection Site Pain' },
  { value: 'other', label: 'Other' },
];

const SymptomsPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<Symptom[]>([]);
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 16));
  const [category, setCategory] = useState<string>('nausea');
  const [severity, setSeverity] = useState<number>(0);
  const [note, setNote] = useState<string>('');
  const [error, setError] = useState<string>('');

  const token = localStorage.getItem('token');

  const fetchItems = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/symptoms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setItems(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const addItem = async () => {
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/symptoms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date: new Date(date).toISOString(), category, severity, note })
      });
      if (!res.ok) throw new Error('Failed to save');
      setDate(new Date().toISOString().slice(0, 16));
      setCategory('nausea');
      setSeverity(0);
      setNote('');
      fetchItems();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    }
  };

  const deleteItem = async (id?: string) => {
    if (!id) return;
    await fetch(`${API_BASE_URL}/symptoms/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchItems();
  };

  const averageSeverity = useMemo(() => {
    if (!items.length) return 0;
    return Number((items.reduce((s, x) => s + (x.severity || 0), 0) / items.length).toFixed(1));
  }, [items]);

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-emerald-700 mb-4">Symptoms</h1>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="p-4 rounded-xl border border-emerald-200 bg-white shadow-sm">
          <h2 className="text-lg font-semibold mb-3">Log a Symptom</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Date & Time</label>
              <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Severity (0–10)</label>
              <input type="range" min={0} max={10} value={severity} onChange={e => setSeverity(Number(e.target.value))} className="w-full" />
              <div className="text-sm text-gray-600 mt-1">Current: <b>{severity}</b></div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes (optional)</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="Context, triggers, etc." />
            </div>
            {!!error && <div className="text-red-600 text-sm">{error}</div>}
            <button onClick={addItem} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg">Add Symptom</button>
          </div>
        </div>

        <div className="p-4 rounded-xl border border-emerald-200 bg-white shadow-sm">
          <h2 className="text-lg font-semibold mb-3">History</h2>
          <div className="text-sm text-gray-600 mb-3">Entries: {items.length} • Avg severity: {averageSeverity}</div>
          <div className="max-h-[420px] overflow-auto divide-y">
            {items.map(item => (
              <div key={item._id} className="py-3 flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-stone-800">{new Date(item.date).toLocaleString()}</div>
                  <div className="text-sm text-stone-600">{CATEGORIES.find(c => c.value === item.category)?.label || item.category} • Severity {item.severity}</div>
                  {item.note && <div className="text-sm text-stone-500 mt-1">{item.note}</div>}
                </div>
                <button onClick={() => deleteItem(item._id)} className="text-red-600 text-sm hover:underline">Delete</button>
              </div>
            ))}
            {!items.length && <div className="text-sm text-stone-500">No symptoms logged yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SymptomsPage;


